import React, { ReactNode } from 'react'
import Styles from './VisualStyleInstagramPreview.module.scss'
import Silhouette from '../Previews/InstagramPreview/silhouette.jpg'
import { useVisualStylePreviewCaption } from 'hooks/queries/visualStyles/useVisualStylePreviewCaption'

interface VisualStyleInstagramPreviewProps {
  imageUrl: string
  accountName?: string
  avatarUrl?: string
  captionElement?: ReactNode
  brandKitId?: number
  brandVoiceId?: number
  brandKitName?: string
}

const LikeIcon = () => (
  <svg xmlns="http://www.w3.org/2000/svg" width="16" height="14" viewBox="0 0 16 14" fill="none">
    <path
      fillRule="evenodd"
      clipRule="evenodd"
      d="M1.61123 1.62932C2.29044 0.966786 3.21153 0.594597 4.17193 0.594597C5.13234 0.594597 6.05342 0.966786 6.73263 1.62932L7.79386 2.66391L8.85508 1.62932C9.18919 1.29178 9.58885 1.02255 10.0307 0.837332C10.4726 0.652117 10.9479 0.554626 11.4288 0.550548C11.9097 0.546471 12.3867 0.635888 12.8318 0.813583C13.2769 0.991278 13.6813 1.25369 14.0214 1.58551C14.3614 1.91733 14.6304 2.31191 14.8125 2.74624C14.9946 3.18056 15.0863 3.64592 15.0821 4.11516C15.0779 4.58441 14.978 5.04815 14.7882 5.47932C14.5983 5.91048 14.3224 6.30045 13.9765 6.62645L7.79386 12.6599L1.61123 6.62645C0.932226 5.96372 0.550781 5.06498 0.550781 4.12788C0.550781 3.19078 0.932226 2.29205 1.61123 1.62932V1.62932Z"
      stroke="white"
      strokeOpacity="0.6"
      strokeWidth="1.10087"
      strokeLinejoin="round"
    />
  </svg>
)

const CommentIcon = () => (
  <svg xmlns="http://www.w3.org/2000/svg" width="17" height="17" viewBox="0 0 17 17" fill="none">
    <path
      d="M13.6117 10.5189C13.9423 9.7709 14.126 8.9434 14.126 8.07301C14.126 4.72906 11.4154 2.01825 8.07177 2.01825C4.72813 2.01825 2.01758 4.72906 2.01758 8.07301C2.01758 11.417 4.72813 14.1278 8.07177 14.1278C9.14828 14.1278 10.1592 13.8468 11.0351 13.3541L14.1271 14.1272L13.6117 10.5189Z"
      stroke="white"
      strokeOpacity="0.6"
      strokeWidth="1.10087"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
)

const ShareIcon = () => (
  <svg xmlns="http://www.w3.org/2000/svg" width="17" height="17" viewBox="0 0 17 17" fill="none">
    <path
      d="M14.3685 2.94484L5.5819 8.01777M1.87815 3.29339L13.6723 2.44101C14.3986 2.38852 14.8653 3.19692 14.4567 3.79968L7.82146 13.5875C7.3669 14.258 6.32338 14.0001 6.23349 13.1951L5.69332 8.35692C5.66634 8.11528 5.53967 7.89589 5.3439 7.75171L1.42402 4.86483C0.771746 4.38444 1.07017 3.35178 1.87815 3.29339Z"
      stroke="white"
      strokeOpacity="0.6"
      strokeWidth="1.10087"
      strokeLinecap="round"
    />
  </svg>
)

const BookmarkIcon = () => (
  <svg xmlns="http://www.w3.org/2000/svg" width="10" height="14" viewBox="0 0 10 14" fill="none">
    <path
      d="M0.550781 1.12014C0.550781 0.805491 0.805858 0.550415 1.12051 0.550415H8.72682C9.04147 0.550415 9.29655 0.805491 9.29655 1.12014V12.3417C9.29655 12.4648 9.15101 12.5299 9.05923 12.4479L4.92367 8.75367L0.788102 12.4479C0.696324 12.5299 0.550781 12.4648 0.550781 12.3417V1.12014Z"
      stroke="white"
      strokeOpacity="0.6"
      strokeWidth="1.10087"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
)

export const VisualStyleInstagramPreview = ({
  imageUrl,
  accountName = 'Your Account',
  avatarUrl,
  captionElement,
  brandKitId,
  brandVoiceId,
  brandKitName,
}: VisualStyleInstagramPreviewProps) => {
  const { caption, isGenerating } = useVisualStylePreviewCaption({
    brandKitId,
    brandVoiceId,
    brandKitName,
    enabled: !!brandKitId,
  })

  return (
    <div className={Styles.preview}>
      <div className={Styles.header}>
        <img src={avatarUrl || Silhouette} alt="Avatar" className={Styles.avatar} />
        <div className={Styles.info}>
          <div className={Styles.username}>{accountName}</div>
          <div className={Styles.timestamp}>Just now</div>
        </div>
      </div>

      <div className={Styles.imageContainer}>
        <img src={imageUrl} alt="Post" className={Styles.image} />
        {captionElement && <div className={Styles.captionOverlay}>{captionElement}</div>}
      </div>

      <div className={Styles.content}>
        <div className={Styles.actions}>
          <div className={Styles.actionsLeft}>
            <LikeIcon />
            <CommentIcon />
            <ShareIcon />
          </div>
          <div className={Styles.actionsRight}>
            <BookmarkIcon />
          </div>
        </div>

        {isGenerating || !caption ? (
          <div className={Styles.caption}>
            <strong className={Styles.captionUsername}>{accountName}</strong>
            <div className={Styles.captionLoading}>
              <div className={Styles.loadingBar} />
              <div className={Styles.loadingBar} style={{ width: '85%' }} />
            </div>
          </div>
        ) : (
          <div className={Styles.caption}>
            <strong className={Styles.captionUsername}>{accountName}</strong> {caption}
            <span className={Styles.more}>...more</span>
          </div>
        )}
      </div>
    </div>
  )
}
